/**
 * Blossom — collection.js
 * Result-list helpers for the collection and search pages.
 *
 * <load-more>  — "Load more" button and infinite scroll
 *   (snippets/results-pagination.liquid). Fetches the next page of the same
 *   section through the Section Rendering API and appends its
 *   [data-results-item] elements into the page's [data-results-grid].
 *   Infinite scroll auto-triggers via IntersectionObserver but always keeps
 *   the real button (and a plain link without JavaScript) as a fallback.
 *
 * <read-more>  — collapsible SEO/long description text with a toggle that only
 *   appears when the content overflows.
 */
(() => {
  if (!window.Theme) return;

  if (!customElements.get('load-more')) {
    class LoadMore extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('[data-load-more-button]');
        if (!this.button || this.bound) return;
        this.bound = true;

        this.button.addEventListener('click', (event) => {
          event.preventDefault();
          this.load();
        });

        if (this.dataset.infinite === 'true' && 'IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            (entries) => {
              if (entries.some((entry) => entry.isIntersecting)) this.load({ auto: true });
            },
            { rootMargin: '0px 0px 600px 0px' }
          );
          this.observer.observe(this);
        }
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.controller?.abort();
      }

      get grid() {
        const scope = this.closest('[data-facets-results]') || this.closest('section') || document;
        return scope.querySelector('[data-results-grid]');
      }

      async load({ auto = false } = {}) {
        const url = this.button?.getAttribute('href');
        const grid = this.grid;
        if (this.loading || !url || !grid) return;

        this.loading = true;
        const keyboardUser = !auto && document.activeElement === this.button;
        this.controller = new AbortController();
        this.button.classList.add('is-loading');
        this.button.setAttribute('aria-busy', 'true');

        try {
          const doc = await Theme.fetchSection(url, this.dataset.sectionId, { signal: this.controller.signal });
          const sourceGrid = doc.querySelector('[data-results-grid]');
          const items = sourceGrid ? Array.from(sourceGrid.querySelectorAll(':scope > [data-results-item]')) : [];
          const fragment = document.createDocumentFragment();
          items.forEach((item) => fragment.appendChild(document.importNode(item, true)));
          const firstNew = fragment.firstElementChild;
          grid.appendChild(fragment);
          Theme.initReveal?.(grid);

          const message = (this.dataset.loadedMessage || '').replace('[count]', items.length);
          const pagination = this.closest('[data-results-pagination]');
          const nextPagination = doc.querySelector('[data-results-pagination]');
          if (pagination && nextPagination) pagination.innerHTML = nextPagination.innerHTML;

          if (message) Theme.announce(message);
          if (keyboardUser && firstNew) {
            const focusTarget = firstNew.querySelector('a[href], button:not([disabled])');
            focusTarget?.focus();
          }
        } catch (error) {
          if (error.name === 'AbortError') return;
          // Stop auto-loading after a failure; the button stays usable.
          this.observer?.disconnect();
          Theme.toast(error.message, { type: 'error' });
        } finally {
          this.loading = false;
          if (this.isConnected && this.button) {
            this.button.classList.remove('is-loading');
            this.button.removeAttribute('aria-busy');
          }
        }
      }
    }

    customElements.define('load-more', LoadMore);
  }

  if (!customElements.get('read-more')) {
    class ReadMore extends HTMLElement {
      connectedCallback() {
        this.content = this.querySelector('.read-more__content');
        this.toggle = this.querySelector('.read-more__toggle');
        if (!this.content || !this.toggle || this.bound) return;
        this.bound = true;
        this.classList.add('is-collapsible');

        this.toggle.addEventListener('click', () => {
          const expanded = this.toggle.getAttribute('aria-expanded') !== 'true';
          this.toggle.setAttribute('aria-expanded', String(expanded));
          this.classList.toggle('is-expanded', expanded);
          this.toggle.textContent = expanded ? this.toggle.dataset.labelLess : this.toggle.dataset.labelMore;
        });

        this.check();
        this.resizeObserver = new ResizeObserver(Theme.debounce(() => this.check(), 150));
        this.resizeObserver.observe(this.content);
      }

      disconnectedCallback() {
        this.resizeObserver?.disconnect();
      }

      check() {
        if (this.classList.contains('is-expanded')) return;
        const overflowing = this.content.scrollHeight > this.content.clientHeight + 4;
        this.toggle.hidden = !overflowing;
        this.classList.toggle('is-overflowing', overflowing);
      }
    }

    customElements.define('read-more', ReadMore);
  }
})();
